import PropTypes, { bool, func } from "prop-types";
import {
  Children,
  cloneElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styled from "styled-components/macro";

import classNames from "classnames";
import { motion } from "framer-motion";

import FnrDeleteButton from "UI/FnrDeleteButton";
import FnrErrorText from "UI/FnrErrorText";
import FnrIcon from "UI/FnrIcon";
import FnrIconButton from "UI/FnrIconButton";
import FnrOpacityToggle from "UI/FnrOpacityToggle";
import { colors } from "UI/colors";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";

import { colorIsLight, lightenDarkenColor } from "Utilities/Colors/colorUtils";
import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";

import { useCTAContext } from "./CTA";

/**
 * CTAWrapper component.
 *
 * @param {Object} props - Component props.
 * @param {string} props.action - The action to display on the CTA.
 * @param {string} props.icon - The icon to display on the CTA.
 * @param {string} props.color - The color scheme of the CTA.
 * @param {boolean} props.isAdmin - Whether the user is an admin.
 * @param {boolean} props.isDeleting - Whether the CTA is currently being deleted.
 * @param {string} props.deleteErrorMessage - The error message to display if the CTA deletion fails.
 * @param {function} props.handleDelete - The function to call when the CTA is deleted.
 * @param {function} props.handleEdit - The function to call when the CTA is edited.
 * @param {ReactNode} props.children - The children of the CTA.
 * @param {boolean} props.fullWidth - Full width override.
 *
 * @returns {JSX.Element} The CTAWrapper component.
 */
const CTAWrapper = ({
  action,
  children,
  icon,
  color = "fbeaff",
  isAdmin,
  isDeleting,
  deleteErrorMessage,
  handleDelete,
  handleEdit,
  moveBackward,
  moveForward,
  fullWidth,
  isPreview,
  warningMessage,
}) => {
  const [toggleActive, setToggleActive] = useState(false);
  const containerRef = useRef();
  const leftArrowRef = useRef();
  const rightArrowRef = useRef();
  const editRef = useRef();
  const deleteRef = useRef();
  const localIsHoverFocused = useIsHoverFocused(
    containerRef,
    leftArrowRef,
    rightArrowRef,
    editRef,
    deleteRef
  );
  const { isHovering, setIsHovering } = useCTAContext();
  const lightColor = lightenDarkenColor(color || "333", 40);
  const darkColor = lightenDarkenColor(color || "333", -40);
  const gradientBackground = `linear-gradient(to bottom right, #${lightColor}, #${darkColor})`;

  useEffect(() => {
    setIsHovering(Boolean(localIsHoverFocused));
  }, [localIsHoverFocused, setIsHovering]);

  const toggleActiveStart = () => {
    setToggleActive(true);
  };
  const toggleActiveEnd = () => {
    setToggleActive(false);
  };

  const text_dark = useMemo(() => colorIsLight(color), [color]);

  const animationVariants = {
    hidden: { opacity: 0, scale: 0.7 },
    visible: {
      opacity: 1,
      scale: 1,
    },
  };

  return (
    <Wrapper
      className={classNames({ text_dark })}
      whileTap={!isAdmin && !isPreview && { scale: action ? 0.97 : 1 }}
      style={action && !isPreview && { cursor: "pointer" }}
      variants={animationVariants}
      $fullWidth={fullWidth || isPreview}
    >
      <Background
        $color={gradientBackground}
        animate={
          action && !isAdmin && !isPreview && isHovering
            ? {
                margin: -8,
              }
            : null
        }
      >
        {icon && (
          <Icon>
            <IconInside>
              <FnrIcon icon={icon} />
            </IconInside>
          </Icon>
        )}
      </Background>
      <Inside ref={containerRef}>
        {Children.map(children, (child) =>
          child ? cloneElement(child) : null
        )}
        {isAdmin && (
          <SFnrOpacityToggle show={isHovering || toggleActive}>
            <FnrHorizontalGap gap={1}>
              {moveBackward && (
                <SFnrIconButton
                  icon={fullWidth ? "arrow-up fas" : "arrow-left fas"}
                  onClick={() => {
                    moveBackward();
                    leftArrowRef.current.blur();
                  }}
                  aria-label="reorder-call-to-action-backward"
                  size="sm"
                  ref={leftArrowRef}
                />
              )}
              {moveForward && (
                <SFnrIconButton
                  icon={fullWidth ? "arrow-down fas" : "arrow-right fas"}
                  onClick={() => {
                    moveForward();
                    rightArrowRef.current.blur();
                  }}
                  aria-label="reorder-call-to-action-forward"
                  size="sm"
                  ref={rightArrowRef}
                />
              )}
              <SFnrIconButton
                icon="pencil fas"
                onClick={handleEdit}
                aria-label="edit-call-to-action"
                size="sm"
                ref={editRef}
              />
              <FnrDeleteButton
                onFocus={toggleActiveStart}
                isLoading={isDeleting}
                onClick={handleDelete}
                variant="iconButtonWithBackground"
                confirmText="Are you sure you want to delete this? It cannot be undone."
                size="sm"
                cancelSideEffect={toggleActiveEnd}
                popoverWidth={250}
                ref={deleteRef}
              />
            </FnrHorizontalGap>
          </SFnrOpacityToggle>
        )}
        {warningMessage && (
          <WarningMessage onClick={handleEdit}>
            <FnrIcon icon="exclamation-triangle fas" />
            {warningMessage}
          </WarningMessage>
        )}
        <FnrErrorText error={!deleteErrorMessage}>
          {deleteErrorMessage}
        </FnrErrorText>
      </Inside>
    </Wrapper>
  );
};

export default CTAWrapper;

CTAWrapper.propTypes = {
  action: PropTypes.string,
  icon: PropTypes.string,
  color: PropTypes.string,
  isAdmin: bool,
  isDeleting: bool,
  deleteErrorMessage: PropTypes.string,
  handleDelete: PropTypes.func,
  handleEdit: PropTypes.func,
  moveForward: func,
  moveBackward: func,
  fullWidth: bool,
  isPreview: bool,
  warningMessage: PropTypes.string,
  children: PropTypes.node,
};

const Wrapper = styled(motion.div)`
  flex-grow: 1;
  margin: 8px;
  position: relative;
  z-index: 0;
  min-height: 220px;
  /* percentages chosen to work well with padding/margin of exisiting code (would love to change) */
  width: ${({ $fullWidth }) => ($fullWidth ? "97%" : "30%")};
  min-width: 280px;
  box-sizing: border-box;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  color: #fff;

  &.text_dark {
    color: #383838;
  }
`;

const Background = styled(motion.div)`
  position: absolute;
  z-index: -1;
  inset: 0;
  overflow: hidden;
  background: ${({ $color }) => $color || "#333"};
  box-shadow: rgba(0, 0, 0, 0.4) 1px 2px 12px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Inside = styled(motion.div)`
  z-index: 2;
  display: flex;
  width: 100%;
`;
const Icon = styled.div`
  position: absolute;
  color: #fff;
  font-size: 228px;
  height: 100%;
  padding: 0 40px;
  display: flex;
  align-items: center;
  opacity: 0.08;
  z-index: 1;
  margin-left: -60px;
  ${Wrapper}.text_dark & {
    opacity: 0.15;
  }
`;
const IconInside = styled.div`
  transform: rotate(-6deg);
`;

const SFnrOpacityToggle = styled(FnrOpacityToggle)`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: ${colors.fnGray100};
  z-index: 100;
  border-radius: 24px;
  padding: 2px;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.2);
`;

const SFnrIconButton = styled(FnrIconButton)`
  background-color: ${colors.fnGray100};
`;

const WarningMessage = styled.button`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 100;
  border-radius: 24px;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  padding: 6px 12px;
  color: ${colors.fnRedNotice};
  background-color: ${colors.fnYellowNotice};
  font-size: inherit;
  border: none;
  cursor: pointer;
`;
